import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  BarChart3,
  BarChart2,
  Server,
  LogOut,
  Menu,
  X,
  Activity,
  RefreshCw,
  Stethoscope,
  Copy,
  Workflow,
  AlertTriangle,
  Boxes,
  Database,
  Merge,
  BrainCircuit,
  Bot,
  BookOpen,
  Globe,
  Sparkles,
  Layers3,
  FileCheck2,
} from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import { useAuth } from '../contexts/AuthContext';
import AuthService from '../services/auth';
import SEO from './SEO';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: ${colors.background.primary};
  display: flex;
`;

const Sidebar = styled.aside.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  width: 280px;
  background: ${colors.white};
  border-right: 1px solid ${colors.border.light};
  padding: ${spacing[6]};
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  overflow-y: auto;
  z-index: 100;
  transition: transform 0.3s ease;
  
  @media (max-width: ${breakpoints.md}) {
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
  }
`;

const SidebarHeader = styled.div`
  margin-bottom: ${spacing[6]};
  padding-bottom: ${spacing[4]};
  border-bottom: 2px solid ${colors.border.light};
`;

const SidebarTitle = styled.h1`
  font-size: ${typography.fontSizes.xl};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
`;

const UserInfo = styled.div`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  margin-top: ${spacing[2]};
`;

const UserRole = styled.span`
  display: inline-block;
  padding: ${spacing[1]} ${spacing[2]};
  background: ${colors.primary[100]};
  color: ${colors.primary[700]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  margin-top: ${spacing[2]};
`;

const Nav = styled.nav`
  margin-bottom: ${spacing[6]};
`;

const NavSection = styled.div`
  margin-bottom: ${spacing[6]};
`;

const NavSectionTitle = styled.h3`
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${spacing[3]};
`;

const NavItem = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${props => props.isActive ? colors.primary[50] : 'transparent'};
  color: ${props => props.isActive ? colors.primary[600] : colors.text.secondary};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  
  &:hover {
    background: ${props => props.isActive ? colors.primary[100] : colors.background.secondary};
    color: ${props => props.isActive ? colors.primary[700] : colors.text.primary};
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${colors.error[50]};
  color: ${colors.error[600]};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.error[100]};
    color: ${colors.error[700]};
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  margin-left: 280px;
  padding: ${spacing[6]};
  
  @media (max-width: ${breakpoints.md}) {
    margin-left: 0;
    padding: ${spacing[20]} ${spacing[4]} ${spacing[4]};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  top: ${spacing[4]};
  left: ${spacing[4]};
  z-index: 101;
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]};
  cursor: pointer;
  box-shadow: ${shadows.md};
  
  &:hover {
    background: ${colors.background.secondary};
  }
  
  @media (max-width: ${breakpoints.md}) {
    display: block;
  }
`;

const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: ${breakpoints.md}) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

const ContentHeader = styled.div`
  margin-bottom: ${spacing[8]};
`;

const PageTitle = styled.h2`
  font-size: ${typography.fontSizes['3xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
`;

const PageDescription = styled.p`
  font-size: ${typography.fontSizes.base};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing[6]};
`;

interface AdminLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  pageTitle: string;
  pageDescription: string;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  currentPage,
  pageTitle,
  pageDescription,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { userInfo } = useAuth();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    AuthService.logout('/');
  };

  return (
    <AdminContainer>
      <SEO
        title={`${pageTitle} | Admin | Event Finder`}
        description={pageDescription}
        noIndex={true}
      />
      <MobileMenuButton
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? 'Close admin navigation' : 'Open admin navigation'}
        title={sidebarOpen ? 'Close admin navigation' : 'Open admin navigation'}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </MobileMenuButton>
      
      <Overlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <Sidebar isOpen={sidebarOpen}>
        <SidebarHeader>
          <SidebarTitle>Admin Panel</SidebarTitle>
          <UserInfo>
            {userInfo.userName || userInfo.userId}
            <br />
            <UserRole>
              {userInfo.isAdmin ? 'Administrator' : 'Contributor'}
            </UserRole>
          </UserInfo>
        </SidebarHeader>

        <Nav>
          <NavSection>
            <NavSectionTitle>Overview</NavSectionTitle>
            <NavItem 
              isActive={currentPage === 'dashboard'}
              onClick={() => handleNavigation('/admin')}
            >
              <BarChart3 size={18} />
              Dashboard
            </NavItem>
            <NavItem
              isActive={currentPage === 'systemInsights'}
              onClick={() => handleNavigation('/admin/system-insights')}
            >
              <Activity size={18} />
              System Insights
            </NavItem>
          </NavSection>

          <NavSection>
            <NavSectionTitle>Event Pipeline</NavSectionTitle>
            <NavItem
              isActive={currentPage === 'howItWorks'}
              onClick={() => handleNavigation('/admin/how-it-works')}
            >
              <BookOpen size={18} />
              How It Works
            </NavItem>
            <NavItem
              isActive={currentPage === 'refreshEventSources'}
              onClick={() => handleNavigation('/admin/refresh-event-sources')}
            >
              <Globe size={18} />
              1. Refresh Event Sources
            </NavItem>
            <NavItem
              isActive={currentPage === 'extractEvents'}
              onClick={() => handleNavigation('/admin/extract-events')}
            >
              <Sparkles size={18} />
              2. Extract Events
            </NavItem>
            <NavItem
              isActive={currentPage === 'extractionBatches'}
              onClick={() => handleNavigation('/admin/batches')}
            >
              <Layers3 size={18} />
              AI Batches
            </NavItem>
            {process.env.NODE_ENV === 'development' && userInfo.isAdmin && (
              <NavItem
                isActive={currentPage === 'batchVerification'}
                onClick={() => handleNavigation('/admin/batches/verify')}
              >
                <FileCheck2 size={18} />
                Verify Batch Quality
              </NavItem>
            )}
          </NavSection>

          <NavSection>
            <NavSectionTitle>Catalog</NavSectionTitle>
            <NavItem
              isActive={currentPage === 'catalog'}
              onClick={() => handleNavigation('/admin/catalog')}
            >
              <Database size={18} />
              Entities, Sources & Events
            </NavItem>
            <NavItem
              isActive={currentPage === 'mergeEntities'}
              onClick={() => handleNavigation('/admin/merge-entities')}
            >
              <Merge size={18} />
              Merge Entities
            </NavItem>
            <NavItem
              isActive={currentPage === 'aiDecisions'}
              onClick={() => handleNavigation('/admin/ai-decisions')}
            >
              <BrainCircuit size={18} />
              AI Decisions
            </NavItem>
            <NavItem
              isActive={currentPage === '/admin/agent'}
              onClick={() => handleNavigation('/admin/agent')}
            >
              <Bot size={18} />
              Agent
            </NavItem>
          </NavSection>

          <NavSection>
            <NavSectionTitle>Index Operations</NavSectionTitle>
            <NavItem
              isActive={currentPage === 'searchHealth'}
              onClick={() => handleNavigation('/admin/search-health')}
            >
              <Stethoscope size={18} />
              Search Index Health
            </NavItem>
            <NavItem
              isActive={currentPage === 'syncIndex'}
              onClick={() => handleNavigation('/admin/sync-index')}
            >
              <RefreshCw size={18} />
              Synchronize Index
            </NavItem>
            <NavItem
              isActive={currentPage === 'cleanupOrphans'}
              onClick={() => handleNavigation('/admin/cleanup-orphans')}
            >
              <Trash2 size={18} />
              Remove Orphans
            </NavItem>
            <NavItem
              isActive={currentPage === 'duplicates'}
              onClick={() => handleNavigation('/admin/duplicates')}
            >
              <Copy size={18} />
              Duplicate Events
            </NavItem>
            <NavItem
              isActive={currentPage === 'processingRuns'}
              onClick={() => handleNavigation('/admin/processing-runs')}
            >
              <BarChart2 size={18} />
              Processing Runs
            </NavItem>
            <NavItem
              isActive={currentPage === 'nightlyRuns'}
              onClick={() => handleNavigation('/admin/nightly-runs')}
            >
              <Activity size={18} />
              Nightly Runs
            </NavItem>
            <NavItem
              isActive={currentPage === 'workflowMetadata'}
              onClick={() => handleNavigation('/admin/workflow-metadata')}
            >
              <Workflow size={18} />
              Workflow Runs
            </NavItem>
            <NavItem
              isActive={currentPage === 'wipeEvents'}
              onClick={() => handleNavigation('/admin/wipe-events')}
            >
              <AlertTriangle size={18} />
              Wipe Events
            </NavItem>
          </NavSection>

          <NavSection>
            <NavSectionTitle>Cache</NavSectionTitle>
            <NavItem
              isActive={currentPage === 'cache'}
              onClick={() => handleNavigation('/admin/cache')}
            >
              <Boxes size={18} />
              Cache Layer
            </NavItem>
          </NavSection>

          <NavSection>
            <NavSectionTitle>Azure Functions</NavSectionTitle>
            <NavItem 
              isActive={currentPage === 'functions'}
              onClick={() => handleNavigation('/admin/functions')}
            >
              <Server size={18} />
              Functions Status
            </NavItem>
          </NavSection>
        </Nav>

        <LogoutButton onClick={handleLogout}>
          <LogOut size={18} />
          Sign Out
        </LogoutButton>
      </Sidebar>

      <MainContent>
        <ContentHeader>
          <PageTitle>{pageTitle}</PageTitle>
          <PageDescription>{pageDescription}</PageDescription>
        </ContentHeader>
        {children}
      </MainContent>
    </AdminContainer>
  );
};

export { ContentGrid };
